import { map, get, filter, merge } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import { GraphTemplateV2 } from "app/components/widget/dashboard/GraphTemplate-v2";
import type { IWidgetModelTypesFamily } from "app/components/widget/widget-types/Widget";
import { CHART_COLORS } from "app/constants/ChartColors";
import { isDurationAfterSplit } from "app/pages/website-analysis/traffic-sources/mmx/components/common/MobileSplit-utils";
import { dashboardMmxTooltipPositioner, tooltipPositioner } from "app/services/HighchartsPositioner";
import { fetchWebsiteImageQuery } from "app/services/sitesService/queries/sitesQueries";
import { trafficChannelsService } from "app/services/TrafficChannelsService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { getLegendLabel } from "../widget-utilities/getLegendLabel";

import { GraphWidget } from "./GraphWidget";

/**
 * Created by Eyal.Albilia
 */
export class MmxChannelsGraphDashboardWidget extends GraphWidget {
    public isPptSupported = () => {
        const hasData = this.chartConfig.series && this.chartConfig.series.length > 0;
        const hasOptions = !!this.metadata.chartOptions;
        return hasData && hasOptions;
    };

    constructor() {
        super();
    }

    static getWidgetMetadataType() {
        return "MmxChannelsGraphDashboard";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    get templateUrl() {
        return `/app/components/widget/widget-templates/graph.html`;
    }

    get templateComponent() {
        return GraphTemplateV2;
    }

    protected async getLegendResources(siteList: string[]) {
        const keyItems = (this._viewData.key || []) as Array<{ name?: string; icon?: string; image?: string }>;
        const knownIcons = new Map<string, string>();
        for (const site of keyItems) {
            const icon = site.icon || site.image;
            if (site.name && icon) {
                knownIcons.set(site.name, icon);
            }
        }

        const icons = await Promise.all(
            siteList.map(async (domain) => {
                const known = knownIcons.get(domain);
                if (known) {
                    return known;
                }
                try {
                    const response = await fetchWebsiteImageQuery({ website: domain });
                    return response.image || "";
                } catch {
                    return "";
                }
            }),
        );

        return siteList.map((rootDomain, index) => ({
            id: rootDomain,
            name: rootDomain,
            color: CHART_COLORS.chartMainColors[index],
            icon: icons[index],
            smallIcon: true,
            type: "Website" as IWidgetModelTypesFamily,
            index,
            legendClass: "legend-smallHeight",
        }));
    }

    getLegendItems() {
        return this.legendItems;
    }

    protected updateLegendItems() {
        this.setUtilityData("legendItems", this.legendItems);
    }

    protected getChartOptions(formattedData): any {
        const defaultChartOptions = super.getChartOptions(formattedData);

        if (this.dashboardId === DASHBOARD_ID_PREVIEW_MODE) {
            return {
                ...defaultChartOptions,
                legend: {
                    itemDistance: 11,
                    labelFormatter: function () {
                        return getLegendLabel({
                            color: this.color,
                            type: "horizontal",
                            name: this.name,
                            className: "mmx-dashboard-preview-legend",
                        });
                    },
                },
            };
        } else {
            return defaultChartOptions;
        }
    }

    protected async getChartSeries(unorderedData: any): Promise<any[]> {
        const selectedChannel = this._widgetConfig.properties.selectedChannel;
        const selectedSites = this._viewData.key?.map((site) => site.name);
        const widgetWebSource = this._widgetModel.webSource;
        const widgetDuration = this._widgetModel.duration;
        const isUnifiedSearchChannels = trafficChannelsService.isUnifiedSearchChannelsType(
            widgetWebSource,
            widgetDuration,
        );
        if (this._widgetConfig.properties.selectedChannel == undefined || selectedSites.length == 1) {
            this._widgetConfig.properties.selectedChannel = undefined;
            const trafficChannels = trafficChannelsService.getAvailableTrafficChannels(isUnifiedSearchChannels);
            let orderedData = await super.getChartSeries(unorderedData);
            orderedData.forEach((elem) => {
                const innerOrder = map(filter(trafficChannels, { id: elem.name }), "innerOrder");
                elem.legendIndex =
                    innerOrder && innerOrder.length > 0
                        ? map(filter(trafficChannels, { id: elem.name }), "innerOrder")[0]
                        : 0;
                elem.color = CHART_COLORS.chartMainColors[elem.legendIndex];
                elem.marker = {
                    ...elem.marker,
                    symbol: "circle",
                };
            });
            orderedData = orderedData.sort((a, b) => {
                return a.legendIndex - b.legendIndex;
            });
            return orderedData;
        }

        this.legendItems = await this.getLegendResources(selectedSites as string[]);
        this.updateLegendItems();
        let index = 0;
        const selectedChannelBEName = trafficChannelsService.getChannelById(selectedChannel, isUnifiedSearchChannels);
        return selectedSites.map((rootDomain) => {
            return {
                name: rootDomain,
                showInLegend: false,
                color: CHART_COLORS.chartMainColors[index],
                seriesName: rootDomain,
                data: super.formatSeries(
                    get(unorderedData, `["${rootDomain}"]["${selectedChannelBEName.name}"][0]`, []),
                ),
                marker: {
                    symbol: "circle",
                    lineWidth: 1,
                    color: CHART_COLORS.chartMainColors[index],
                },
                index: index++,
            };
        });
    }

    protected _getTitle() {
        if (this._widgetConfig.properties.titleTemplate == "custom") {
            return super._getTitle();
        }
        const selectedChannel = this._widgetConfig.properties.selectedChannel;
        let title = super._getTitle();
        title = trafficChannelsService.removeChannelNameInTitle(title);
        if (selectedChannel && this._widgetConfig.properties.key.length > 1) {
            let isUnifiedSearchChannels = false;
            if (this.webSource !== devicesTypes.DESKTOP) {
                if (!isDurationAfterSplit(this.durationObject.forAPI.from)) {
                    isUnifiedSearchChannels = true;
                }
            }
            const i18selected = trafficChannelsService.getChannelById(selectedChannel, isUnifiedSearchChannels);
            if (i18selected) {
                title += " " + i18selected.text;
            } else {
                Sentry.errorTracker({
                    message: "MmxChannelsGraphDashboardWidget: Error choosing channel",
                    e: new Error("MmxChannelsGraphDashboardWidget: Error choosing channel"),
                    level: "error",
                    params: {
                        extra: {
                            selectedChannel,
                        },
                    },
                });
            }
        }
        return title;
    }

    getHighChartsConfig(chartOptions, formattedData) {
        return this._highchartsConfig.lineGraphWidget(
            merge({}, chartOptions, {
                tooltip: {
                    positioner: this.isCompare() ? tooltipPositioner : dashboardMmxTooltipPositioner,
                },
            }),
            formattedData,
        );
    }

    onResize() {
        if (this.chartConfig.options && this.chartConfig.options.chart) {
            if (this.pos.sizeY === 2) {
                this.chartConfig.options.chart.height = 540;
            } else {
                if (this.isCompare()) {
                    this.chartConfig.options.chart.height = 200;
                } else {
                    this.chartConfig.options.chart.height = 230;
                }
            }
        }
        if (this.chartInstance && typeof this.chartInstance.reflow === "function") {
            this.chartInstance.reflow();
        }
    }

    _getProUrlParams() {
        if (this.isCompare()) {
            return Object.assign(super._getProUrlParams(), {
                channelAnalysisChannel: this._widgetConfig.properties.selectedChannel,
            });
        }
        return super._getProUrlParams();
    }
}

MmxChannelsGraphDashboardWidget.register();
